import React, { forwardRef, PropsWithChildren, useEffect } from 'react';
import { MarketplaceTemplateEnrichedWithCreatorName } from '../../domain/entities/marketplaceTemplate';

import s from './MarketplaceTemplate.scss';
import { useTranslation } from '@wix/wix-i18n-config';
import { Button } from '@wix/design-system';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import { useInViewPort } from '../../hooks/useInViewPort';

interface MarketplaceTemplateProps {
  marketplaceTemplate: MarketplaceTemplateEnrichedWithCreatorName;
  index?: number;
}

export const MarketplaceTemplate = forwardRef<
  HTMLDivElement,
  PropsWithChildren<MarketplaceTemplateProps>
>(({ marketplaceTemplate, index }, ref) => {
  const { t } = useTranslation();
  const biLogger = useBiLogger();
  const { slug } = useSelectedCategory() ?? {};
  const inViewPort = useInViewPort(ref as any, { threshold: 1 });

  const viewDetailsUrl = `https://www.wix.com/marketplace/template/${marketplaceTemplate.id}`;

  const onViewClick = () => {
    biLogger.reportGeneralAction({
      action: 'open',
      actionValue: 'view_details_marketplace',
      templateId: marketplaceTemplate.id,
      index,
      categorySlug: slug,
    });
  };

  useEffect(() => {
    if (!inViewPort) {
      return;
    }
    biLogger.reportAboveFoldImpression({
      tabName: 'Template Marketplace',
      templateId: marketplaceTemplate.id,
      title: marketplaceTemplate.title,
      slug: '',
      categorySlug: slug,
      index,
    });
  }, [biLogger, inViewPort, index, marketplaceTemplate, slug]);

  return (
    <div className={s.template} ref={ref}>
      <div className={s.thumbnailWrapper}>
        <div className={s.buttonsOverlay}>
          <div className={s.buttonsContainer}>
            <Button
              as="a"
              priority="secondary"
              size="large"
              target="_blank"
              skin="light"
              className={s.viewDetailsButton}
              href={viewDetailsUrl}
              onClick={onViewClick}
            >
              {t('app.templates.marketplace.gallery.template.viewDetails')}
            </Button>
          </div>
        </div>
        <img
          src={marketplaceTemplate.thumbnailUrl}
          alt={marketplaceTemplate.title}
          className={s.thumbnail}
        />
      </div>
      <div className={s.row}>
        <div className={s.title}>{marketplaceTemplate.title}</div>
        <div className={s.price}>
          {marketplaceTemplate.price === 0
            ? t('app.templates.marketplace.gallery.template.price.free')
            : `$${marketplaceTemplate.price}`}
        </div>
      </div>
      <div className={s.creator}>
        {t('app.templates.marketplace.gallery.template.createdBy', {
          creator: marketplaceTemplate.creatorName,
        })}
      </div>
    </div>
  );
});
